<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { Switch, SwitchButton, RefreshRight } from '@element-plus/icons-vue';
import { useAuthStore } from '@/stores/auth';
import { setLocale } from '@/i18n';
import type { NavSection } from '@/layouts/nav';

const props = defineProps<{ navSections: NavSection[] }>();

const router = useRouter();
const auth = useAuthStore();
const { t, locale } = useI18n();

// Visibility is owned here; MainLayout calls open()/close() via the exposed API.
const visible = ref(false);
const query = ref('');
const activeIndex = ref(0);
const inputRef = ref<{ focus: () => void } | null>(null);
// The element that had focus before we opened — restored on close (a11y).
let previouslyFocused: HTMLElement | null = null;

type CommandIcon = NavSection['items'][number]['icon'];
interface Command {
  id: string;
  /** i18n key OR (for actions) a pre-resolved label flag */
  label: string;
  group: string;
  icon: CommandIcon;
  run: () => void;
}

// Navigation commands — derived from the same navSections the sidebar renders.
const navCommands = computed<Command[]>(() =>
  props.navSections.flatMap((section) => {
    const group = section.title ? t(section.title) : t('nav.dashboard');
    const entries = section.entry ? [section.entry] : [];
    return [...entries, ...section.items].map((item) => ({
      id: `nav:${item.name}`,
      label: t(item.label),
      group,
      icon: item.icon,
      run: () => router.push(item.to ?? { name: item.name }),
    }));
  }),
);

// A small set of action commands beyond pure navigation.
const actionCommands = computed<Command[]>(() => [
  {
    id: 'action:toggle-locale',
    label: t('commandPalette.action_toggle_locale'),
    group: t('commandPalette.group_actions'),
    icon: Switch,
    run: () => setLocale(locale.value === 'zh-CN' ? 'en-US' : 'zh-CN'),
  },
  {
    id: 'action:reload',
    label: t('commandPalette.action_reload'),
    group: t('commandPalette.group_actions'),
    icon: RefreshRight,
    run: () => window.location.reload(),
  },
  {
    id: 'action:logout',
    label: t('commandPalette.action_logout'),
    group: t('commandPalette.group_actions'),
    icon: SwitchButton,
    run: async () => {
      await auth.logout();
      router.replace({ name: 'internal-login', query: { logged_out: '1' } });
    },
  },
]);

const allCommands = computed<Command[]>(() => [...navCommands.value, ...actionCommands.value]);

// Case-insensitive substring filter over the visible label.
const filtered = computed<Command[]>(() => {
  const q = query.value.trim().toLowerCase();
  if (!q) return allCommands.value;
  return allCommands.value.filter((c) => c.label.toLowerCase().includes(q));
});

// Keep highlight in bounds whenever the result set changes.
watch(filtered, () => {
  activeIndex.value = 0;
});

function open() {
  if (visible.value) return;
  previouslyFocused = document.activeElement as HTMLElement | null;
  visible.value = true;
  query.value = '';
  activeIndex.value = 0;
  nextTick(() => inputRef.value?.focus());
}

function close() {
  if (!visible.value) return;
  visible.value = false;
  query.value = '';
  // Restore focus to wherever the user was (a11y).
  nextTick(() => previouslyFocused?.focus?.());
}

function runCommand(cmd: Command | undefined) {
  if (!cmd) return;
  close();
  cmd.run();
}

function moveHighlight(delta: number) {
  const len = filtered.value.length;
  if (len === 0) return;
  activeIndex.value = (activeIndex.value + delta + len) % len;
  scrollActiveIntoView();
}

const listRef = ref<HTMLElement | null>(null);
function scrollActiveIntoView() {
  nextTick(() => {
    const el = listRef.value?.querySelector<HTMLElement>('.cmd-item--active');
    el?.scrollIntoView({ block: 'nearest' });
  });
}

function onKeydown(e: KeyboardEvent) {
  if (e.key === 'ArrowDown') {
    e.preventDefault();
    moveHighlight(1);
  } else if (e.key === 'ArrowUp') {
    e.preventDefault();
    moveHighlight(-1);
  } else if (e.key === 'Enter') {
    e.preventDefault();
    runCommand(filtered.value[activeIndex.value]);
  } else if (e.key === 'Escape') {
    e.preventDefault();
    close();
  } else if (e.key === 'Tab') {
    // Focus is trapped on the input — the only focusable control — so swallow Tab.
    e.preventDefault();
  }
}

defineExpose({ open, close });
</script>

<template>
  <Teleport to="body">
    <div v-if="visible" class="cmd-overlay" @click.self="close">
      <div
        class="cmd-panel"
        role="dialog"
        aria-modal="true"
        :aria-label="t('commandPalette.placeholder')"
        @keydown="onKeydown"
      >
        <input
          ref="inputRef"
          v-model="query"
          class="cmd-input"
          type="text"
          autocomplete="off"
          spellcheck="false"
          :placeholder="t('commandPalette.placeholder')"
        />
        <div ref="listRef" class="cmd-list">
          <template v-if="filtered.length">
            <button
              v-for="(cmd, idx) in filtered"
              :key="cmd.id"
              type="button"
              class="cmd-item"
              :class="{ 'cmd-item--active': idx === activeIndex }"
              @mouseenter="activeIndex = idx"
              @click="runCommand(cmd)"
            >
              <el-icon :size="16" class="cmd-item__icon">
                <component :is="cmd.icon" />
              </el-icon>
              <span class="cmd-item__label">{{ cmd.label }}</span>
              <span class="cmd-item__group">{{ cmd.group }}</span>
            </button>
          </template>
          <div v-else class="cmd-empty">{{ t('commandPalette.empty') }}</div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<style scoped>
.cmd-overlay {
  position: fixed;
  inset: 0;
  z-index: 3000;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding-top: 12vh;
  background: rgba(15, 23, 42, 0.4);
  backdrop-filter: blur(2px);
}
.cmd-panel {
  width: 100%;
  max-width: 560px;
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0 20px 50px rgba(15, 23, 42, 0.25);
  overflow: hidden;
  display: flex;
  flex-direction: column;
}
.cmd-input {
  width: 100%;
  box-sizing: border-box;
  border: none;
  outline: none;
  padding: 18px 20px;
  font-size: 16px;
  color: #0f172a;
  border-bottom: 1px solid #e2e8f0;
  font-family: inherit;
}
.cmd-input::placeholder {
  color: #94a3b8;
}
.cmd-list {
  max-height: 50vh;
  overflow-y: auto;
  padding: 8px;
}
.cmd-item {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 10px 12px;
  border: none;
  background: none;
  border-radius: 8px;
  cursor: pointer;
  text-align: left;
  font-family: inherit;
  font-size: 14px;
  color: #334155;
}
.cmd-item--active {
  background: #eff6ff;
  color: #2563eb;
}
.cmd-item__icon {
  color: #94a3b8;
  flex-shrink: 0;
}
.cmd-item--active .cmd-item__icon {
  color: #2563eb;
}
.cmd-item__label {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-weight: 500;
}
.cmd-item__group {
  flex-shrink: 0;
  font-size: 12px;
  color: #94a3b8;
}
.cmd-empty {
  padding: 28px 12px;
  text-align: center;
  color: #94a3b8;
  font-size: 14px;
}
</style>
